import { cleanTokenData, loadTokenData } from "@/lib/auth/helpers";

function normalizeApiBase(raw: string): string {
  return raw.replace(/\/+$/, "").replace(/\/auth$/, "");
}

const API_BASE_URL = normalizeApiBase(import.meta.env.VITE_API_BASE_URL ?? "");

/** Optional override for /assets/* only (local northstar-api during dev). */
export const ASSETS_API_BASE_URL = normalizeApiBase(
  import.meta.env.VITE_ASSETS_API_BASE_URL ?? import.meta.env.VITE_API_BASE_URL ?? "",
);

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

export interface PaginatedResponse<T> {
  items: T[];
  pagination: {
    page: number;
    limit: number;
    totalCount: number;
    totalPages: number;
  };
}

/**
 * Authenticated fetch wrapper. Reads the Northstar JWT from local storage and
 * attaches it as a Bearer token. On 401 the user is redirected to /login —
 * there is no refresh-token flow in v1, the user re-runs the Okta PKCE flow.
 */
export async function apiFetch<T>(
  path: string,
  init?: RequestInit,
  baseUrl: string = API_BASE_URL,
): Promise<T> {
  const tokenData = loadTokenData();
  const token = tokenData?.accessToken ?? "";
  const url = `${baseUrl}${path}`;

  const res = await fetch(url, {
    ...init,
    headers: {
      "Content-Type": "application/json",
      ...(token && { Authorization: `Bearer ${token}` }),
      ...init?.headers,
    },
  });

  if (res.status === 401) {
    cleanTokenData();
    if (
      typeof window !== "undefined" &&
      !window.location.pathname.startsWith("/login")
    ) {
      window.location.href = "/login";
    }
    throw new ApiError(401, "Unauthorized");
  }

  if (!res.ok) {
    const body = await res.text().catch(() => "Unknown error");
    throw new ApiError(res.status, body);
  }

  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}
